import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { MatSort, MatPaginator, MatSelectChange } from '@angular/material';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { first } from 'rxjs/operators/first';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { combineLatest } from 'rxjs/observable/combineLatest';

import { format, isBefore, isDate, parse } from 'date-fns';

import { DataTableSource } from '../../classes/DataTableSource/datatablesource';

import { ApplicationService, IssueAlertInfo } from '../../services/application/application.service';
import { AuthService } from '../../services/authentication/auth.service';
import { DialogService } from '../../services/dialogs/dialogs.service';
import { DownloadService, DownloadFormat, HttpMethod } from '../../services/download/download.service';
import { ErrorService } from '../../services/error/error.service';
import { SplashService } from '../../services/splash/splash.service';
import { UserInfo, UserService } from '../../services/user/user.service';

import { DateFnsPipe } from '../../pipes/datefns.pipe';
import { TitleCasePipe } from '../../pipes/titlecase.pipe';

import { validateDate } from '../../validators/date.validator';

@Component({
    selector: 'app-issue-alert',
    styleUrls: ['issuealert.component.css'],
    templateUrl: 'issuealert.component.html',
})
export class IssueAlertComponent implements OnInit, OnDestroy {
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sorter: MatSort;

    private _subrelease: Subject<void> = new Subject<void>();

    FilteredAlertsList: IssueAlertInfo[];
    AlertsData: DataTableSource<IssueAlertInfo>;
    AlertsList: IssueAlertInfo[];
    AllHeaders: string[] = [
        'IssueAlertNumber',
        'AlertMessage',
        'DateBegin',
        'DateEnd',
        'InEffect',
        'AddedBy',
        'LastUpdated',
        'Actions',
    ];
    AlertsDisplayMode: string;
    InEffectCount: number;
    NewAlertForm: FormGroup;
    NoData: boolean;
    ViewReady: boolean;
    ShowingNewAlertForm: boolean;
    SmallDisplay: boolean;
    User: UserInfo;
    VisibleHeaders: string[];
    VisibleColumns: string[];

    constructor(
        private appService: ApplicationService,
        private authService: AuthService,
        private dateFNSPipe: DateFnsPipe,
        private dialogService: DialogService,
        private downloadService: DownloadService,
        private errorService: ErrorService,
        private fb: FormBuilder,
        private mediaChange: ObservableMedia,
        private splashService: SplashService,
        private titleCasePipe: TitleCasePipe,
        private userService: UserService
    ) {}

    ngOnInit() {
        const self = this;

        self.FilteredAlertsList = null;
        self.AlertsDisplayMode = 'A';
        self.AlertsData = new DataTableSource<IssueAlertInfo>([], self.paginator, self.sorter, self.SortAlerts.bind(self));
        self.AlertsList = null;
        self.InEffectCount = 0;
        self.NoData = false;
        self.ViewReady = false;
        self.ShowingNewAlertForm = false;
        self.SmallDisplay = false;
        self.User = null;

        self.SmallDisplay = self.mediaChange.isActive('xs') || self.mediaChange.isActive('sm');

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subrelease))
            .subscribe(media => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
            });

        // Get the first non-null UserInfo object so we can begin setting up the component
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(user => user && !!user.AuthToken)
        ).subscribe(user => {
            self.User = user;

            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_user => {
                self.User = _user;
            });

            // Load the issue alerts in the database
            self.appService.GetAllWebAppIssueAlerts().subscribe(
                alerts => {
                    self.UpdateAlerts(alerts);

                    // Make the form for creating new alerts
                    self.NewAlertForm = self.fb.group({
                        WebAppIssueAlertID: [null],
                        AlertMessage: [null, Validators.required],
                        DateBegin: [null, [Validators.required, validateDate]],
                        DateEnd: [null, [Validators.required, validateDate]],
                        IsUpdate: [false],
                    });

                    // Ready for component use.
                    self.ViewReady = true;
                },
                error => {
                    self.errorService.HandleServiceError(error);
                }
            );
        });
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    // #region Add New Alert Method

    AddNewAlert() {
        const self = this;

        const form = self.NewAlertForm.value;

        // Get confirmation
        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText:
                    `You are about to add the following new issue alert:<br /><br />` +
                    `<strong>Alert Message: </strong><br /><em>${form.AlertMessage}</em><br />` +
                    `<strong>Display Begin Date: </strong>${self.dateFNSPipe.transform(form.DateBegin, 'M/D/YYYY h:mm A')}<br />` +
                    `<strong>Display End Date: </strong>${self.dateFNSPipe.transform(form.DateEnd, 'M/D/YYYY h:mm A')}<br /><br />` +
                    `Once added, the new issue alert will be broadcast on the login screen and the Issue Reporting Tool ` +
                    `during the window in which it is set to be in effect. Are you sure you wish to add this issue alert?`,
                OKText: 'Yes - Add',
                CancelText: 'No - Cancel',
            })
            .subscribe(result => {
                if (result === true) {
                    // User is proceeding. Add the new issue alert.
                    const dbegin = parse(form.DateBegin);
                    const dend = parse(form.DateEnd);

                    self.appService
                        .AddWebAppIssueAlert(self.User.AuthToken, self.User.CurrentRole, form.AlertMessage, dbegin, dend)
                        .subscribe(
                            alerts => {
                                // Update the alerts.
                                self.UpdateAlerts(alerts);

                                // Clear the new issue alert form.
                                self.NewAlertForm.reset();

                                // Snackbar success
                                self.splashService.SnackbarSuccess('Issue Alert Added Successfully.');
                            },
                            error => {
                                self.errorService.HandleServiceError(error);
                            }
                        );
                }
            });
    }

    // #endregion

    // #region Disable Alert Method

    DisableAlert(alert: IssueAlertInfo) {
        const self = this;

        if (!alert) {
            return;
        }

        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText:
                    `You are about to disable the following issue alert in the DELTA System:<br /><br />` +
                    `<strong>Issue Alert ID #: </strong>${alert.WebAppIssueAlertID}<br />` +
                    `<strong>Alert Message: </strong><br /><em>${alert.AlertMessage}</em><br />` +
                    `<strong>Display Begin Date: </strong>${self.dateFNSPipe.transform(alert.DateBegin, 'M/D/YYYY h:mm A')}<br />` +
                    `<strong>Display End Date: </strong>${self.dateFNSPipe.transform(alert.DateEnd, 'M/D/YYYY h:mm A')}<br /><br />` +
                    `<strong>Last Modified By: </strong>${self.titleCasePipe.transform(alert.AddedByFirstName)} ` +
                    `${self.titleCasePipe.transform(alert.AddedByLastName)} (SAPID ${alert.AddedBySAPID})<br />` +
                    `<strong>Last Modified On: </strong>${self.dateFNSPipe.transform(
                        alert.LastUpdated,
                        'M/D/YYYY h:mm A'
                    )}<br /><br />` +
                    `Once disabled, this issue alert will no longer be displayed in the DELTA System but will not be ` +
                    `removed from the system, in case you wish to reactivate it later. Are you sure you wish to continue?`,
                OKText: 'Yes - Disable',
                CancelText: 'No - Cancel',
            })
            .subscribe(result => {
                if (result === true) {
                    // Disable the alert by setting the end date to the start date
                    self.appService
                        .UpdateWebAppIssueAlert(
                            self.User.AuthToken,
                            self.User.CurrentRole,
                            alert.WebAppIssueAlertID,
                            alert.AlertMessage,
                            alert.DateBegin,
                            alert.DateBegin
                        )
                        .subscribe(
                            alerts => {
                                // Update the alerts.
                                self.UpdateAlerts(alerts);

                                // Clear the new issue alert form.
                                self.NewAlertForm.reset();

                                // Snackbar success
                                self.splashService.SnackbarSuccess('Issue Alert Disabled Successfully.');
                            },
                            error => {
                                self.errorService.HandleServiceError(error);
                            }
                        );
                }
            });
    }

    // #endregion

    // #region Load Update Form Method

    LoadUpdateForm(alert: IssueAlertInfo) {
        const self = this;

        if (!alert) {
            return;
        }

        // Patch the form with the values passed in
        const patch = {
            WebAppIssueAlertID: alert.WebAppIssueAlertID,
            AlertMessage: alert.AlertMessage,
            DateBegin: format(alert.DateBegin, 'M/D/YYYY h:mm A'),
            DateEnd: format(alert.DateEnd, 'M/D/YYYY h:mm A'),
            IsUpdate: true,
        };

        self.NewAlertForm.patchValue(patch);
    }

    // #endregion

    // #region Remove Alert Method

    RemoveAlert(alert: IssueAlertInfo) {
        const self = this;

        if (!alert) {
            return;
        }

        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText: `You are about to remove the following issue alert from the DELTA System:<br /><br />
        <strong>Issue Alert ID #: </strong>${alert.WebAppIssueAlertID}<br />
        <strong>Alert Message: </strong><br />
        <em>${alert.AlertMessage}</em><br />
        <strong>Display Begin Date: </strong>${self.dateFNSPipe.transform(alert.DateBegin, 'M/D/YYYY h:mm A')}<br />
        <strong>Display End Date: </strong>${self.dateFNSPipe.transform(alert.DateEnd, 'M/D/YYYY h:mm A')}<br /><br />
        <strong>Last Modified By: </strong>${self.titleCasePipe.transform(alert.AddedByFirstName)} ${self.titleCasePipe.transform(
                    alert.AddedByLastName
                )} (SAPID ${alert.AddedBySAPID})<br />
        <strong>Last Modified On: </strong>${self.dateFNSPipe.transform(alert.LastUpdated, 'M/D/YYYY h:mm A')}<br /><br />
        Once removed, this issue alert will no longer be displayed in the DELTA System. Are you sure you wish to continue?`,
                OKText: 'Yes - Remove',
                CancelText: 'No - Cancel',
            })
            .subscribe(result => {
                if (result === true) {
                    // Remove the alert
                    self.appService
                        .RemoveWebAppIssueAlert(self.User.AuthToken, self.User.CurrentRole, alert.WebAppIssueAlertID)
                        .subscribe(
                            alerts => {
                                // Update the alerts.
                                self.UpdateAlerts(alerts);

                                // Snackbar success
                                self.splashService.SnackbarSuccess('Issue Alert Removed Successfully.');
                            },
                            error => {
                                self.errorService.HandleServiceError(error);
                            }
                        );
                }
            });
    }

    // #endregion

    // #region Sort Alerts Method

    SortAlerts(First: IssueAlertInfo, Second: IssueAlertInfo) {
        const self = this;

        let PropA: string | number | Date;
        let PropB: string | number | Date;

        switch (self.sorter.active) {
            case 'IssueAlertNumber':
                [PropA, PropB] = [First.WebAppIssueAlertID, Second.WebAppIssueAlertID];
                break;
            case 'AlertMessage':
                [PropA, PropB] = [First.AlertMessage, Second.AlertMessage];
                break;
            case 'DateBegin':
                [PropA, PropB] = [First.DateBegin, Second.DateBegin];
                break;
            case 'DateEnd':
                [PropA, PropB] = [First.DateEnd, Second.DateEnd];
                break;
            case 'InEffect':
                [PropA, PropB] = [First.InEffect ? 'Yes' : 'No', Second.InEffect ? 'Yes' : 'No'];
                break;
            case 'AddedBy':
                [PropA, PropB] = [
                    `${First.AddedByLastName}, ${First.AddedByFirstName}`,
                    `${Second.AddedByLastName}, ${Second.AddedByFirstName}`,
                ];
                break;
            case 'LastUpdated':
                [PropA, PropB] = [First.LastUpdated, Second.LastUpdated];
                break;
        }

        if (isDate(PropA) || isDate(PropB)) {
            let baseSort = 0;

            if (isDate(PropA) && !isDate(PropB)) {
                baseSort = 1;
            } else if (isDate(PropB) && !isDate(PropA)) {
                baseSort = -1;
            } else if (isBefore(PropA, PropB)) {
                baseSort = -1;
            } else if (isBefore(PropB, PropA)) {
                baseSort = 1;
            }

            return baseSort * (self.sorter.direction === 'asc' ? 1 : -1);
        } else {
            const ValueA = PropA != null && PropA !== '' ? (isNaN(+PropA) ? PropA : +PropA) : PropA;
            const ValueB = PropB != null && PropB !== '' ? (isNaN(+PropB) ? PropB : +PropB) : PropB;

            return (ValueA < ValueB ? -1 : 1) * (self.sorter.direction === 'asc' ? 1 : -1);
        }
    }

    // #endregion

    // #region Update Alert Display Mode Method

    UpdateAlertDisplayMode(event: MatSelectChange) {
        const self = this;

        if (!event || !event.value) {
            return;
        }

        // Update the alerts by triggering the filtering
        self.UpdateAlerts(self.AlertsList);
    }

    // #endregion

    // #region Update Alerts Method

    UpdateAlerts(alerts: IssueAlertInfo[]) {
        const self = this;

        self.AlertsList = alerts;
        self.InEffectCount = (alerts || []).filter(item => item.InEffect === true).length || 0;

        // Apply filtering.
        self.FilteredAlertsList = (self.AlertsList || []).filter(item => {
            return (
                self.AlertsDisplayMode === 'A' ||
                (self.AlertsDisplayMode === 'I' && item.InEffect === true) ||
                (self.AlertsDisplayMode === 'X' && item.InEffect !== true)
            );
        });

        if (!self.FilteredAlertsList || !self.FilteredAlertsList.length) {
            self.NoData = true;

            // Put in a fake record to trip the display message
            self.AlertsData.Update([{}]);
        } else {
            self.NoData = false;
            self.AlertsData.Update(self.FilteredAlertsList);
        }

        self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
        self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
    }

    // #endregion

    // #region Update Existing Alert Method

    UpdateExistingAlert() {
        const self = this;

        const form = self.NewAlertForm.value;

        // Get confirmation
        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText:
                    `You are about to update Alert #${form.WebAppIssueAlertID} to the following new :<br /><br />` +
                    `<strong>Alert Message: </strong><br /><em>${form.AlertMessage}</em><br />` +
                    `<strong>Display Begin Date: </strong>${self.dateFNSPipe.transform(form.DateBegin, 'M/D/YYYY h:mm A')}<br />` +
                    `<strong>Display End Date: </strong>${self.dateFNSPipe.transform(form.DateEnd, 'M/D/YYYY h:mm A')}<br /><br />` +
                    `Once updated, the issue alert will be broadcast on the login screen and the Issue Reporting Tool ` +
                    `(if appropriate) during the window in which it is set to be in effect. Are you sure you wish to update this ` +
                    `issue alert?`,
                OKText: 'Yes - Update',
                CancelText: 'No - Cancel',
            })
            .subscribe(result => {
                if (result === true) {
                    // User is proceeding. Update the issue alert.
                    const dbegin = parse(form.DateBegin);
                    const dend = parse(form.DateEnd);

                    self.appService
                        .UpdateWebAppIssueAlert(
                            self.User.AuthToken,
                            self.User.CurrentRole,
                            form.WebAppIssueAlertID,
                            form.AlertMessage,
                            dbegin,
                            dend
                        )
                        .subscribe(
                            alerts => {
                                // Update the alerts.
                                self.UpdateAlerts(alerts);

                                // Clear the new issue alert form.
                                self.NewAlertForm.reset();

                                // Snackbar success
                                self.splashService.SnackbarSuccess('Issue Alert Updated Successfully.');
                            },
                            error => {
                                self.errorService.HandleServiceError(error);
                            }
                        );
                }
            });
    }

    // #endregion
}

// #region Custom Classes

// #endregion



// WEBPACK FOOTER //
// ./src/app/issuealert/issuealert.component.ts